import React, { useState, useEffect } from 'react';
import { base44 } from 'api/base44Client';
import { Button } from 'Components/ui/button';
import { Input } from 'Components/ui/input';
import { Label } from 'Components/ui/label';
import { Textarea } from 'Components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'Components/ui/select';
import { Loader2, Send, CheckCircle } from 'lucide-react';
import { Card } from '@/Components/ui/card';
import { sendEmail, TEMPLATE_ID_INFO } from '@/utils/emailService';

export const REQUEST_TYPES = {
    VENDITA: { value: 'vendita_ups', label: 'Acquisto UPS' },
    RIPARAZIONE: { value: 'riparazione', label: 'Riparazione' },
    MANUTENZIONE: { value: 'manutenzione', label: 'Contratto Manutenzione' },
    CONSULENZA: { value: 'consulenza', label: 'Consulenza Tecnica' },
    ALTRO: { value: 'altro', label: 'Altro' }
};

const SECTORS = [
    { value: 'ospedale', label: 'Ospedale' },
    { value: 'clinica', label: 'Clinica' },
    { value: 'aeroporto', label: 'Aeroporto' },
    { value: 'supermercato', label: 'Supermercato' },
    { value: 'farmacia', label: 'Farmacia' },
    { value: 'laboratorio', label: 'Laboratorio' },
    { value: 'azienda', label: 'Azienda' },
    { value: 'altro', label: 'Altro' },
];

export default function ContactForm({
    defaultType = 'vendita_ups',
    prefilledMessage = '',
    onSuccess,
    showTitle = true,
    className = ''
}) {
    const [formData, setFormData] = useState({
        nome: '',
        azienda: '',
        email: '',
        telefono: '',
        tipo_richiesta: defaultType,
        settore: '',
        messaggio: prefilledMessage
    });
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    // Update message if prefilledMessage prop changes
    useEffect(() => {
        if (prefilledMessage) {
            setFormData(prev => ({ ...prev, messaggio: prefilledMessage }));
        }
    }, [prefilledMessage]);

    // Update type if defaultType prop changes
    useEffect(() => {
        if (defaultType) {
            setFormData(prev => ({ ...prev, tipo_richiesta: defaultType }));
        }
    }, [defaultType]);


    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        try {
            await base44.entities.RichiestaPreventivo.create({
                ...formData,
                stato: 'nuovo'
            });

            // Send Email
            const typeLabel = Object.values(REQUEST_TYPES).find(t => t.value === formData.tipo_richiesta)?.label || formData.tipo_richiesta;

            const result = await sendEmail({
                ...formData,
                tipo_richiesta: `Contatto - ${typeLabel}`,
                messaggio: `Settore: ${formData.settore || '-'}\n\n${formData.messaggio}`
            }, TEMPLATE_ID_INFO);

            if (!result.success) {
                alert(`Errore invio email: ${result.error?.text || 'Errore sconosciuto'}`);
                setSubmitting(false);
                return;
            }

            setSubmitted(true);

            // Google Analytics Conversion Tracking
            if (typeof window.gtag === 'function') {
                window.gtag('event', 'conversion_event_submit_lead_form');
            }

            if (onSuccess) onSuccess();
        } catch (error) {
            console.error(error);
            alert('Errore nel salvataggio della richiesta.');
        } finally {
            setSubmitting(false);
        }
    };

    if (submitted) {
        return (
            <Card className="p-8 text-center bg-white shadow-none border-0">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Messaggio Inviato!</h3>
                <p className="text-gray-600 mb-6">
                    Grazie per averci contattato. Ti risponderemo al più presto.
                </p>
                <Button
                    onClick={() => {
                        setSubmitted(false);
                        setFormData({
                            nome: '', azienda: '', email: '', telefono: '',
                            tipo_richiesta: defaultType,
                            settore: '',
                            messaggio: prefilledMessage
                        });
                    }}
                    variant="outline"
                    className="rounded-full"
                >
                    Invia un altro messaggio
                </Button>
            </Card>
        );
    }

    return (
        <div className={className}>
            {showTitle && (
                <div className="mb-8">
                    <h2 className="text-3xl font-bold text-gray-900 mb-2">Richiedi Informazioni</h2>
                    <p className="text-gray-600">
                        Compila il form e riceverai una risposta entro 24 ore lavorative
                    </p>
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                        <Label htmlFor="nome">Nome e Cognome *</Label>
                        <Input
                            id="nome"
                            value={formData.nome}
                            onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                            required
                            className="mt-1"
                        />
                    </div>
                    <div>
                        <Label htmlFor="azienda">Azienda</Label>
                        <Input
                            id="azienda"
                            value={formData.azienda}
                            onChange={(e) => setFormData({ ...formData, azienda: e.target.value })}
                            className="mt-1"
                        />
                    </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                        <Label htmlFor="email">Email *</Label>
                        <Input
                            id="email"
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            required
                            className="mt-1"
                        />
                    </div>
                    <div>
                        <Label htmlFor="telefono">Telefono</Label>
                        <Input
                            id="telefono"
                            value={formData.telefono}
                            onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                            className="mt-1"
                        />
                    </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                        <Label htmlFor="tipo">Tipo di Richiesta *</Label>
                        <Select
                            value={formData.tipo_richiesta}
                            onValueChange={(value) => setFormData({ ...formData, tipo_richiesta: value })}
                        >
                            <SelectTrigger className="mt-1">
                                <SelectValue placeholder="Seleziona..." />
                            </SelectTrigger>
                            <SelectContent>
                                {Object.values(REQUEST_TYPES).map(type => (
                                    <SelectItem key={type.value} value={type.value}>
                                        {type.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label htmlFor="settore">Settore</Label>
                        <Select
                            value={formData.settore}
                            onValueChange={(value) => setFormData({ ...formData, settore: value })}
                        >
                            <SelectTrigger className="mt-1">
                                <SelectValue placeholder="Seleziona..." />
                            </SelectTrigger>
                            <SelectContent>
                                {SECTORS.map(sector => (
                                    <SelectItem key={sector.value} value={sector.value}>
                                        {sector.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                <div>
                    <Label htmlFor="messaggio">Messaggio *</Label>
                    <Textarea
                        id="messaggio"
                        value={formData.messaggio}
                        onChange={(e) => setFormData({ ...formData, messaggio: e.target.value })}
                        required
                        rows={5}
                        placeholder="Descrivi le tue esigenze..."
                        className="mt-1"
                    />
                </div>

                <Button
                    type="submit"
                    className="w-full rounded-full bg-blue-600 hover:bg-blue-700 h-12 gap-2 !text-white"
                    disabled={submitting}
                >
                    {submitting ? (
                        <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Invio in corso...
                        </>
                    ) : (
                        <>
                            <Send className="w-4 h-4" />
                            Invia Messaggio
                        </>
                    )}
                </Button>
            </form>
        </div>
    );
}
